// Inventário periódico (regra 8): contagem "cega" no celular (mesma tela da vistoria) e
// conferência pelo financeiro — saldo do sistema na data × contado. Concluir gera um AJUSTE por
// diferença, com o motivo informado. Produto não contado não gera ajuste.
import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, ClipboardList, Plus, RefreshCw } from "lucide-react";
import {
  getEstoqueInventario, getEstoqueInventarios, getRoleFromToken, postEstoqueCancelarInventario,
  postEstoqueConcluirInventario, postEstoqueInventario,
} from "../../api";
import { permissoesEstoque } from "../../constants/estoque";
import { AbaContagem, BarraProgresso, Card } from "./ContagemInventario";
import EstoqueLayout from "./EstoqueLayout";
import { AvisoSemAbertura, JustificativaSaldoNegativo, formatData, hojeISO, mensagemErro, useContextoEstoque } from "./comum";
import { AvisoErro, Campo, Modal, btnStyle, formatBRL, formatQtd, inputStyle, palette, useIsMobile } from "./ui";

function recalcularResumo(itens) {
  const comDif = itens.filter((i) => i.diferenca !== null && Number(i.diferenca) !== 0);
  return {
    total: itens.length,
    contados: itens.filter((i) => i.quantidade_contada !== null).length,
    com_diferenca: comDif.length,
    valor_faltas: comDif.filter((i) => Number(i.diferenca) < 0).reduce((s, i) => s + Number(i.valor_diferenca || 0), 0),
    valor_sobras: comDif.filter((i) => Number(i.diferenca) > 0).reduce((s, i) => s + Number(i.valor_diferenca || 0), 0),
  };
}

function TabelaDiferencas({ C, itens, isMobile, soDiferencas }) {
  const lista = itens.filter((i) => !soDiferencas || (i.diferenca !== null && Number(i.diferenca) !== 0) || i.quantidade_contada === null);
  const corDif = (d) => (d === null ? C.muted : Number(d) < 0 ? C.danger : Number(d) > 0 ? C.success : C.muted);
  if (isMobile) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {lista.map((i) => (
          <div key={i.produto_id} style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>{i.produto_nome}</div>
            <div style={{ display: "flex", gap: 12, fontSize: 12, marginTop: 4, flexWrap: "wrap" }}>
              <span>Sistema: <strong>{formatQtd(i.quantidade_sistema)}</strong></span>
              <span>Contado: <strong>{i.quantidade_contada === null ? "—" : formatQtd(i.quantidade_contada)}</strong></span>
              <span style={{ color: corDif(i.diferenca), fontWeight: 700 }}>
                {i.quantidade_contada === null ? "não contado" : `${Number(i.diferenca) > 0 ? "+" : ""}${formatQtd(i.diferenca)} ${i.unidade_sigla}`}
              </span>
              {i.valor_diferenca !== null && Number(i.diferenca) !== 0 && <span style={{ color: corDif(i.diferenca) }}>{formatBRL(i.valor_diferenca)}</span>}
            </div>
          </div>
        ))}
      </div>
    );
  }
  const th = (a = "right") => ({ padding: "9px 12px", fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", textAlign: a, borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.card, whiteSpace: "nowrap" });
  const td = (a = "right", x = {}) => ({ padding: "8px 12px", fontSize: 13, textAlign: a, borderBottom: `1px solid ${C.border}`, whiteSpace: a === "right" ? "nowrap" : "normal", ...x });
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, overflow: "auto", maxHeight: "58vh" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr><th style={th("left")}>Produto</th><th style={th()}>Sistema</th><th style={th()}>Contado</th><th style={th()}>Diferença</th><th style={th()}>Custo médio</th><th style={th()}>Valor da diferença</th></tr></thead>
        <tbody>
          {lista.length === 0 && <tr><td colSpan={6} style={td("center", { color: C.muted, padding: 20 })}>Nenhuma diferença.</td></tr>}
          {lista.map((i) => (
            <tr key={i.produto_id}>
              <td style={td("left")}>{i.produto_nome}<div style={{ fontSize: 11, color: C.muted }}>{i.categoria_nome}</div></td>
              <td style={td()}>{formatQtd(i.quantidade_sistema)} {i.unidade_sigla}</td>
              <td style={td()}>{i.quantidade_contada === null ? <span style={{ color: C.muted }}>não contado</span> : `${formatQtd(i.quantidade_contada)} ${i.unidade_sigla}`}</td>
              <td style={td("right", { color: corDif(i.diferenca), fontWeight: 700 })}>{i.diferenca === null ? "—" : `${Number(i.diferenca) > 0 ? "+" : ""}${formatQtd(i.diferenca)}`}</td>
              <td style={td()}>{formatBRL(i.custo_medio)}</td>
              <td style={td("right", { color: corDif(i.diferenca), fontWeight: 700 })}>{i.valor_diferenca === null ? "—" : formatBRL(i.valor_diferenca)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Resumo({ C, inv }) {
  const r = inv.resumo;
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 12 }}>
      <Card C={C} style={{ padding: 12 }}><BarraProgresso C={C} feitos={r.contados} total={r.total} rotulo="contados" /></Card>
      <Card C={C} style={{ padding: 12 }}><div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>Com diferença</div><div style={{ fontSize: 20, fontWeight: 800 }}>{r.com_diferenca}</div></Card>
      <Card C={C} style={{ padding: 12 }}><div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>Faltas</div><div style={{ fontSize: 20, fontWeight: 800, color: C.danger }}>{formatBRL(r.valor_faltas)}</div></Card>
      <Card C={C} style={{ padding: 12 }}><div style={{ fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>Sobras</div><div style={{ fontSize: 20, fontWeight: 800, color: C.success }}>{formatBRL(r.valor_sobras)}</div></Card>
    </div>
  );
}

function AbaConferencia({ C, inv, perm, isMobile, onConcluido, onCancelado }) {
  const [soDiferencas, setSoDiferencas] = useState(true);
  const [motivo, setMotivo] = useState("");
  const [justificativa, setJustificativa] = useState("");
  const [confirmando, setConfirmando] = useState(false);
  const [cancelando, setCancelando] = useState(false);
  const [motivoCancelamento, setMotivoCancelamento] = useState("");
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const naoContados = inv.resumo.total - inv.resumo.contados;

  async function executar(fn) {
    setSalvando(true);
    try { await fn(); } catch (e) { setErro(e); } finally { setSalvando(false); setConfirmando(false); }
  }

  return (
    <>
      <AvisoErro C={C} onClose={() => setErro(null)}>{erro && mensagemErro(erro)}</AvisoErro>
      <JustificativaSaldoNegativo C={C} erro={erro} valor={justificativa} onChange={setJustificativa} />
      <Resumo C={C} inv={inv} />
      {naoContados > 0 && (
        <div style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: C.warning, marginBottom: 10 }}>
          <AlertTriangle size={15} /> {naoContados} produto(s) não contado(s) — não geram ajuste e aparecem como pendência no fechamento.
        </div>
      )}
      <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.muted, marginBottom: 8, cursor: "pointer" }}>
        <input type="checkbox" checked={soDiferencas} onChange={(e) => setSoDiferencas(e.target.checked)} /> Só diferenças e não contados
      </label>
      <TabelaDiferencas C={C} itens={inv.itens} isMobile={isMobile} soDiferencas={soDiferencas} />
      <p style={{ fontSize: 12, color: C.muted }}>O saldo do sistema é o de {formatData(inv.data)} e é recalculado a cada abertura desta tela — movimentos lançados com data até o inventário entram na conta.</p>

      {perm.financeiro && (
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", gap: 10 }}>
          <Campo label="Motivo das diferenças" C={C} obrigatorio={inv.resumo.com_diferenca > 0} ajuda="Vai em cada ajuste gerado. Ex.: consumo não lançado, perda, erro de lançamento.">
            <input value={motivo} onChange={(e) => setMotivo(e.target.value)} style={inputStyle(C)} />
          </Campo>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => setConfirmando(true)} disabled={!inv.resumo.contados || (inv.resumo.com_diferenca > 0 && !motivo.trim())}
              style={{ ...btnStyle(C.success), flex: "1 1 220px", opacity: !inv.resumo.contados || (inv.resumo.com_diferenca > 0 && !motivo.trim()) ? 0.5 : 1 }}>
              <CheckCircle2 size={16} /> Concluir inventário
            </button>
            <button onClick={() => setCancelando(true)} style={btnStyle(C.danger, true)}>Cancelar inventário</button>
          </div>
        </div>
      )}

      {confirmando && (
        <Modal C={C} titulo="Concluir inventário?" onClose={() => setConfirmando(false)} largura={440}
          rodape={<>
            <button onClick={() => setConfirmando(false)} style={btnStyle(C.muted, true)}>Voltar</button>
            <button disabled={salvando} onClick={() => executar(async () => { await postEstoqueConcluirInventario(inv.id, { motivo: motivo.trim() || null, justificativa_saldo_negativo: justificativa.trim() || null }); onConcluido(); })} style={btnStyle(C.success)}>
              {salvando ? "Concluindo..." : "Concluir"}
            </button>
          </>}>
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.5 }}>
            {inv.resumo.com_diferenca
              ? <>Serão gerados <strong>{inv.resumo.com_diferenca} ajuste(s)</strong> em {formatData(inv.data)}: faltas de <strong>{formatBRL(inv.resumo.valor_faltas)}</strong> e sobras de <strong>{formatBRL(inv.resumo.valor_sobras)}</strong> (valores finais pelo custo médio da data).</>
              : "Nenhuma diferença encontrada — o inventário será concluído sem ajustes."}
            {" "}Depois de concluído, o inventário não pode mais ser alterado.
          </p>
        </Modal>
      )}
      {cancelando && (
        <Modal C={C} titulo="Cancelar inventário?" onClose={() => setCancelando(false)} largura={440}
          rodape={<>
            <button onClick={() => setCancelando(false)} style={btnStyle(C.muted, true)}>Voltar</button>
            <button disabled={!motivoCancelamento.trim() || salvando} onClick={() => executar(async () => { await postEstoqueCancelarInventario(inv.id, motivoCancelamento.trim()); setCancelando(false); onCancelado(); })} style={{ ...btnStyle(C.danger), opacity: motivoCancelamento.trim() ? 1 : 0.5 }}>Cancelar inventário</button>
          </>}>
          <Campo label="Motivo" C={C} obrigatorio><input value={motivoCancelamento} onChange={(e) => setMotivoCancelamento(e.target.value)} style={inputStyle(C)} autoFocus /></Campo>
        </Modal>
      )}
    </>
  );
}

function NovoInventario({ C, contexto, onCriado }) {
  const [data, setData] = useState(hojeISO());
  const [observacao, setObservacao] = useState("");
  const [erro, setErro] = useState(null);
  const [salvando, setSalvando] = useState(false);
  async function criar() {
    setSalvando(true);
    try { onCriado(await postEstoqueInventario({ data, observacao: observacao.trim() || null })); } catch (e) { setErro(e); setSalvando(false); }
  }
  return (
    <Card C={C} style={{ maxWidth: 560, marginBottom: 16 }}>
      <h3 style={{ margin: "0 0 6px", fontSize: 15 }}>Novo inventário</h3>
      <p style={{ margin: "0 0 12px", fontSize: 12, color: C.muted, lineHeight: 1.5 }}>Conte o estoque físico. As diferenças para o saldo do sistema viram ajustes quando o financeiro concluir.</p>
      <AvisoErro C={C} onClose={() => setErro(null)}>{erro && mensagemErro(erro)}</AvisoErro>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12 }}>
        <Campo label="Data da contagem" C={C} obrigatorio><input type="date" value={data} min={contexto.data_abertura || undefined} max={hojeISO()} onChange={(e) => setData(e.target.value)} style={inputStyle(C)} /></Campo>
        <Campo label="Observação" C={C}><input value={observacao} onChange={(e) => setObservacao(e.target.value)} placeholder="Ex.: contagem de fim de mês" style={inputStyle(C)} /></Campo>
      </div>
      <button onClick={criar} disabled={salvando} style={{ ...btnStyle(C.brand), marginTop: 12, width: "100%", opacity: salvando ? 0.6 : 1 }}><Plus size={15} /> {salvando ? "Iniciando..." : "Iniciar inventário"}</button>
    </Card>
  );
}

export default function InventariosView() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const isMobile = useIsMobile();
  const perm = permissoesEstoque(getRoleFromToken());
  const { contexto, erro: erroContexto } = useContextoEstoque();
  const [lista, setLista] = useState(null);
  const [inv, setInv] = useState(null);
  const [aba, setAba] = useState("contagem");
  const [erro, setErro] = useState("");

  const carregarLista = useCallback(() => getEstoqueInventarios().then(setLista).catch((e) => setErro(e.message)), []);
  const abrir = useCallback((id) => getEstoqueInventario(id).then((d) => { setInv(d); setAba(d.status === "ABERTO" ? "contagem" : "conferencia"); }).catch((e) => setErro(e.message)), []);
  useEffect(() => { carregarLista(); }, [carregarLista]);
  useEffect(() => {
    const aberto = lista?.find((i) => i.status === "ABERTO");
    if (aberto && !inv) abrir(aberto.id);
  }, [lista, inv, abrir]);

  const onItemSalvo = useCallback((item) => setInv((atual) => {
    const itens = atual.itens.map((i) => (i.produto_id === item.produto_id ? item : i));
    return { ...atual, itens, resumo: recalcularResumo(itens) };
  }), []);
  const voltar = () => { setInv(null); carregarLista(); };
  const temAberto = lista?.some((i) => i.status === "ABERTO");

  return (
    <EstoqueLayout titulo="Inventário Periódico" isLight={isLight} setIsLight={setIsLight}
      acoesHeader={inv?.status === "ABERTO" && (
        <button onClick={() => abrir(inv.id)} aria-label="Atualizar" title="Atualizar" style={{ display: "flex", alignItems: "center", background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: 8, minHeight: 38, color: "#fff", cursor: "pointer" }}>
          <RefreshCw size={14} />
        </button>
      )}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro || erroContexto}</AvisoErro>
      {!contexto || !lista ? null : !contexto.abertura_concluida ? <AvisoSemAbertura C={C} /> : inv ? (
        <div style={{ maxWidth: 900 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
            <button onClick={voltar} style={{ ...btnStyle(C.muted, true), padding: "6px 10px", minHeight: 34 }}>← Inventários</button>
            <span style={{ fontSize: 13, color: C.muted }}>
              Inventário de {formatData(inv.data)} · {inv.status === "ABERTO" ? "em andamento" : inv.status === "CONCLUIDO" ? `concluído${inv.concluido_por_nome ? ` por ${inv.concluido_por_nome}` : ""}` : "cancelado"}
            </span>
          </div>
          {inv.status === "ABERTO" ? (
            <>
              {perm.financeiro && (
                <div style={{ display: "flex", gap: 4, marginBottom: 12, borderBottom: `1px solid ${C.border}` }}>
                  {[["contagem", "1. Contagem"], ["conferencia", "2. Conferência"]].map(([id, label]) => (
                    <button key={id} onClick={() => (id === "conferencia" ? abrir(inv.id).then(() => setAba(id)) : setAba(id))}
                      style={{ background: "none", border: "none", borderBottom: `2px solid ${aba === id ? C.brand : "transparent"}`, color: aba === id ? C.brand : C.muted, fontWeight: aba === id ? 700 : 500, fontSize: 14, padding: "10px 12px", minHeight: 44, cursor: "pointer", fontFamily: "inherit" }}>
                      {label}
                    </button>
                  ))}
                </div>
              )}
              {aba === "contagem" || !perm.financeiro
                ? <AbaContagem C={C} inventario={inv} perm={perm} podeEditar onItemSalvo={onItemSalvo} onProdutoCriado={() => abrir(inv.id)} />
                : <AbaConferencia C={C} inv={inv} perm={perm} isMobile={isMobile} onConcluido={() => abrir(inv.id).then(carregarLista)} onCancelado={voltar} />}
            </>
          ) : (
            <>
              {inv.motivo_ajuste && <div style={{ fontSize: 13, marginBottom: 10 }}>Motivo: <em>{inv.motivo_ajuste}</em></div>}
              <Resumo C={C} inv={inv} />
              <TabelaDiferencas C={C} itens={inv.itens} isMobile={isMobile} soDiferencas />
            </>
          )}
        </div>
      ) : (
        <>
          {perm.operador && !temAberto && <NovoInventario C={C} contexto={contexto} onCriado={(d) => { setInv(d); setAba("contagem"); carregarLista(); }} />}
          <h3 style={{ fontSize: 14, margin: "8px 0" }}>Histórico</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 760 }}>
            {lista.length === 0 && <div style={{ fontSize: 13, color: C.muted }}>Nenhum inventário periódico ainda.</div>}
            {lista.map((i) => (
              <button key={i.id} onClick={() => abrir(i.id)} style={{ display: "flex", alignItems: "center", gap: 12, textAlign: "left", background: C.card, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, cursor: "pointer", fontFamily: "inherit", color: C.text, opacity: i.status === "CANCELADO" ? 0.55 : 1 }}>
                <ClipboardList size={20} color={i.status === "ABERTO" ? C.warning : C.brand} />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>Inventário de {formatData(i.data)}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>
                    {i.status === "ABERTO" ? "Em andamento" : i.status === "CONCLUIDO" ? "Concluído" : "Cancelado"} · {i.contados} contado(s) · {i.ajustes} ajuste(s)
                  </div>
                </div>
                {i.status === "CONCLUIDO" && <div style={{ fontSize: 14, fontWeight: 800, color: Number(i.valor_ajustes) < 0 ? C.danger : C.text }}>{formatBRL(i.valor_ajustes)}</div>}
              </button>
            ))}
          </div>
        </>
      )}
    </EstoqueLayout>
  );
}
